import useGetAppliances from '@/api/queries/appliances/useGetAppliances';
import CompanySettingsTabBase from '@/ui/components/companySettings/CompanySettingsTabBase';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { FC, ReactNode } from 'react';
import { Redirect, useParams } from 'react-router';

import {
  estimateSettingsPathForCategory,
  FLOW_SETTINGS_PATH,
  GLOW_SETTINGS_PATH,
} from '@/ui/components/companySettings/estimateSettingsRoutes';
import ApplianceEditorContent from './ApplianceEditorContent';
import ApplianceEditorSkeleton from './components/ApplianceEditorSkeleton';
import { CATEGORY_BY_SLUG, NOUN } from './constants';

const ApplianceEditorPage: FC = () => {
  const { category: categorySlug, applianceId } = useParams<{
    category?: string;
    applianceId?: string;
  }>();
  const { appliances, isLoading } = useGetAppliances();

  const isEdit = applianceId != null;

  let breadcrumbLabel: string;
  let settingsPath = FLOW_SETTINGS_PATH;
  let settingsLabel = 'Flow';
  let body: ReactNode;

  if (isEdit) {
    if (isLoading) {
      breadcrumbLabel = 'Edit appliance';
      body = <ApplianceEditorSkeleton />;
    } else {
      const appliance = appliances?.find((a) => a.id === applianceId);
      if (!appliance) {
        return <Redirect to={FLOW_SETTINGS_PATH} />;
      }
      const noun = NOUN[appliance.productSpec.category] ?? 'appliance';
      breadcrumbLabel = `Edit ${noun}`;
      settingsPath = estimateSettingsPathForCategory(
        appliance.productSpec.category
      );
      settingsLabel = settingsPath === GLOW_SETTINGS_PATH ? 'Glow' : 'Flow';
      body = (
        <ApplianceEditorContent
          category={appliance.productSpec.category}
          appliance={appliance}
        />
      );
    }
  } else {
    const category = categorySlug ? CATEGORY_BY_SLUG[categorySlug] : undefined;
    if (!category) {
      return <Redirect to={FLOW_SETTINGS_PATH} />;
    }
    const noun = NOUN[category] ?? 'appliance';
    breadcrumbLabel = `Add ${noun}`;
    settingsPath = estimateSettingsPathForCategory(category);
    settingsLabel = settingsPath === GLOW_SETTINGS_PATH ? 'Glow' : 'Flow';
    body = <ApplianceEditorContent category={category} />;
  }

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Company settings', to: '/companySettings' },
        { label: settingsLabel, to: settingsPath },
        { label: breadcrumbLabel },
      ]}
    >
      <div className="p-4">
        <CompanySettingsTabBase>{body}</CompanySettingsTabBase>
      </div>
    </AuthenticatedPageWrapper>
  );
};

export default ApplianceEditorPage;
